import {
	Button, Form, Radio, Space,
} from 'antd-mobile';
import { Typography } from 'antd';
import React, {
	useCallback, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';

import { shuffleArray } from '../../libs/utils';
import { useSessionStore } from '../../store';

interface MCQFormValues {
	answer: number
}

interface AttemptMCQProps {
	questionData: AttemptQuestionData
	submitting: boolean
	onSubmit: (data: any) => Promise<void>
	setAttempt: (attemptData: unknown) => void
	getAttempt: () => any | null
	quizData: QuizData | null
}

export const AttemptMCQ: React.FunctionComponent<AttemptMCQProps> = (props) => {
	const {
		questionData, submitting: isSubmitting, onSubmit, quizData,
	} = props;

	const sessionData = useSessionStore((state) => state.sessionData);

	const shuffledOptions = useMemo(() => {
		if (!quizData?.toShuffleMCQOptions) {
			return questionData.questionTypeMCQ?.options?.map((option, index) => ({
				label: option,
				value: index + 1,
			})) ?? [];
		}
		const options = questionData.questionTypeMCQ?.options?.map((option, index) => ({
			label: option,
			value: index + 1,
		})) ?? [];
		return shuffleArray(options, sessionData?.userId ?? '', questionData._id, quizData?.quizId);
	}, [
		quizData?.toShuffleMCQOptions, quizData?.quizId,
		questionData.questionTypeMCQ?.options, questionData._id,
		sessionData?.userId,
	]);

	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

	useEffect(() => {
		setPortalTarget(document.getElementById('mobile-submit-portal'));
	}, []);

	const [form] = Form.useForm();
	const selectedAnswer = Form.useWatch('answer', form);

	const [answerError, setAnswerError] = useState<string | undefined>();

	const handleMCQFormSubmit = useCallback(async (values: MCQFormValues) => {
		await onSubmit({
			inputMCQ: `chkOpt${values.answer}`,
			chk: `chkOpt${values.answer}`,
		});
	}, [onSubmit]);

	const handleMCQFormSubmitFailed = useCallback(async ({ values, errorFields, outOfDate }) => {
		if (errorFields[0].errors[0]) {
			setAnswerError(errorFields[0].errors[0]);
		}
	}, []);

	return (
		<div
			className="attempt-mcq-container"
			style={{
				padding: '0.5em 0 0 1rem',
				borderTop: '1px solid #eeeeee',
			}}
		>
			<Typography.Title level={5}>Select your answer:</Typography.Title>
			<Form
				form={form}
				onFinish={handleMCQFormSubmit}
				onFinishFailed={handleMCQFormSubmitFailed}
				onFieldsChange={() => setAnswerError(undefined)}
				layout="vertical"
				style={{
					'--border-top': 'none',
					'--border-bottom': 'none',
					'--border-inner': 'none',
				}}
			>
				<Form.Item
					name="answer"
					rules={[{ required: true, message: 'Please select an option!' }]}
					initialValue={(
						typeof questionData.questionTypeMCQ?.lastSubmitted === 'number'
							? questionData.questionTypeMCQ.lastSubmitted + 1
							: (questionData.questionTypeMCQ?.lastSubmitted
								&& parseInt(questionData.questionTypeMCQ?.lastSubmitted, 10))
					)}
					style={{ padding: 0 }}
				>
					<Radio.Group
						disabled={isSubmitting}
					>
						<Space direction="vertical" style={{ width: '100%', '--gap': '12px' }}>
							{
								shuffledOptions.map((option, index) => {
									const letter = String.fromCharCode(65 + index);
									const isSelected = selectedAnswer === option.value;
									return (
										<div
											style={{
												border: isSelected ? '1px solid #de6834' : '1px solid #f0f0f0',
												borderRadius: '12px',
												width: '100%',
												backgroundColor: isSelected ? '#fff3f0' : '#fff',
												boxShadow: isSelected ? '0 0 0 1px rgba(222,104,52,0.08)' : 'none',
												transition: 'all 0.2s',
											}}
											key={option.label}
										>
											<Radio
												value={option.value}
												icon={(checked) => (
													<div
														style={{
															width: '32px',
															height: '32px',
															borderRadius: '50%',
															// backgroundColor: checked ? '#fff3f0' : '#f5f5f5',
															color: checked ? '#de6834' : '#8c8c8c',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontWeight: 600,
															marginRight: '12px',
															fontSize: '14px',
														}}
													>
														{letter}
													</div>
												)}
												style={{
													width: '100%',
													padding: '16px',
													'--icon-size': 'auto',
												}}
											>
												<span style={{ fontSize: '15px' }}>{option.label}</span>
											</Radio>
										</div>
									);
								})
							}
						</Space>
					</Radio.Group>
				</Form.Item>
				<Button
					type="button"
					size="small"
					fill="none"
					onClick={() => form.setFieldsValue({
						answer: null,
					})}
					disabled={isSubmitting}
				>
					Clear selection
				</Button>
			</Form>
			{portalTarget ? createPortal(
				<Button
					type="button"
					size="large"
					onClick={() => form.submit()}
					loading={isSubmitting}
					color="primary"
					shape="rounded"
					style={{ padding: '6px 36px', fontSize: '16px', fontWeight: 600 }}
				>
					submit
				</Button>,
				portalTarget,
			) : (
				<div className="submit-mcq-container">
					<Button
						type="button"
						size="large"
						onClick={() => form.submit()}
						loading={isSubmitting}
						color="primary"
						shape="rounded"
					>
						submit
					</Button>
				</div>
			)}
		</div>
	);
};
